import type { NotificationPreference } from '@unifyapps/network/generated/models/notificationPreference';
import type { NotificationPreferenceEventVsPreferences as EventVsPreferences } from '@unifyapps/network/generated/models/notificationPreferenceEventVsPreferences';
import { produce } from 'immer';
import _reduce from 'lodash/reduce';
import { useCallback, useState } from 'react';
import type { NotificationEventType } from '../types';

/*
state shape:
{
  groupA: {
    eventTypeA: {
      IN_APP: true,
      EMAIL: false,
    },
    eventTypeB: {
      IN_APP: true,
      EMAIL: true,
    },
  },
}
  */

export type State = Record<string, Record<string, Record<string, boolean>>>;

const getStateFromPreferences = ({
  preferences,
  events,
  channelTypes,
}: {
  preferences: EventVsPreferences | undefined;
  events: NotificationEventType[];
  channelTypes: { id: string; name: string }[];
}) =>
  events.reduce((acc, event) => {
    const groupName = event.group;
    const eventId = event.id;

    if (!groupName) {
      return acc;
    }

    return produce(acc, (draft) => {
      if (!draft[groupName]) {
        draft[groupName] = {};
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- added safe check
      if (!draft[groupName]?.[eventId]) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- added safe check
        draft[groupName][eventId] = {};
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- added safe check above
      draft[groupName][eventId] = channelTypes.reduce((accum, channel) => {
        accum[channel.id] = preferences?.[eventId]?.includes(channel.id) ?? false;
        return accum;
      }, {});
    });
  }, {});

export const getPreferencesFromState = (
  state: Record<string, Record<string, Record<string, boolean>>>,
) => {
  return _reduce(
    state,
    (acc, group) => {
      const eventTypes = Object.keys(group);

      eventTypes.forEach((eventType) => {
        const enabledChannels = Object.keys(group[eventType]).filter(
          (channel) => group[eventType][channel],
        );

        if (enabledChannels.length) {
          acc[eventType] = enabledChannels;
        }
      });

      return acc;
    },
    {},
  );
};

export const useNotificationPreferencesState = ({
  initialPreferences,
  events,
  channelTypes,
}: {
  initialPreferences: NotificationPreference | undefined;
  events: NotificationEventType[];
  channelTypes: { id: string; name: string }[];
}) => {
  const [preferences, setPreferences] = useState<State>(
    getStateFromPreferences({
      preferences: initialPreferences?.eventVsPreferences,
      events,
      channelTypes,
    }),
  );

  const onToggleEvent = useCallback((groupName: string, eventType: string, channelType: string) => {
    setPreferences((prevPreferences) =>
      produce(prevPreferences, (draft) => {
        draft[groupName][eventType][channelType] = !draft[groupName][eventType][channelType];
      }),
    );
  }, []);

  const onToggleGroup = useCallback((groupName: string, channelType: string) => {
    setPreferences((prevPreferences) =>
      produce(prevPreferences, (draft) => {
        const eventTypesInGroup = Object.keys(draft[groupName]);

        const areAllEventsInGroupSelected = eventTypesInGroup.every(
          (eventType) => draft[groupName][eventType][channelType],
        );

        eventTypesInGroup.forEach((eventType) => {
          draft[groupName][eventType][channelType] = !areAllEventsInGroupSelected;
        });
      }),
    );
  }, []);

  return {
    preferences,
    onToggleEvent,
    onToggleGroup,
  };
};
